import type { Terminal } from "server/dwhApi/terminals/types.js";

import {
  ALL_BUILDINGS_VIEW_KEY,
  NEEDS_ATTENTION_RATE_THRESHOLD,
  NEEDS_ATTENTION_VIEW_KEY,
} from "./constants";
import type { FetchedBuildingSelection, SavedBuildingView } from "./types";

const getAllView = (
  terminals: Terminal[],
  t: (key: string) => string,
): SavedBuildingView => {
  const allIds = terminals.map((term) => term.terminalId);

  return {
    id: ALL_BUILDINGS_VIEW_KEY,
    name: t("portfolioPage:buildingSelectionAll"),
    terminalIds: allIds,
    locked: true,
  };
};

const getNeedsAttentionView = (
  terminals: Terminal[],
  t: (key: string) => string,
) => {
  const attentionIds = terminals
    .filter((term) => term.sortingDegree * 100 < NEEDS_ATTENTION_RATE_THRESHOLD)
    .map((term) => term.terminalId);
  return {
    id: NEEDS_ATTENTION_VIEW_KEY,
    name: t("portfolioPage:buildingSelectionNeedsAttention"),
    terminalIds: attentionIds,
    locked: true,
  };
};

const getCustomViews = (
  terminals: Terminal[],
  buildingSelections?: FetchedBuildingSelection[],
) => {
  const allTerminalIds = terminals.map((t) => t.terminalId);
  return (buildingSelections || [])
    .map((selection) => ({
      id: String(selection.id),
      name: selection.name,
      terminalIds: selection.terminalIds.map(String),
    }))
    .filter((s) => s.terminalIds.every((id) => allTerminalIds.includes(id)));
};

type Props = {
  terminals: Terminal[];
  buildingSelections?: FetchedBuildingSelection[];
  t: (key: string) => string;
};

export const getAllViews = ({ terminals, buildingSelections, t }: Props) => {
  const allView = getAllView(terminals, t);

  const needsAttentionView = getNeedsAttentionView(terminals, t);

  const customViews = getCustomViews(terminals, buildingSelections);

  return [allView, needsAttentionView, ...customViews];
};

export const isSameSelection = (
  a: string[] | null,
  allIds: string[],
  b: string[] | null,
) => {
  const na = a ? [...a].sort() : [...allIds].sort();
  const nb = b ? [...b].sort() : [...allIds].sort();
  return na.length === nb.length && na.every((id, i) => id === nb[i]);
};
